import {
  AccidentAtlasCartesianChartConfig,
  AccidentAtlasPieChartConfig,
} from '../types/api'
import { AccidentAtlasChartRenderType } from '../types/tile'

export type CartesianDataTableRow = {
  category: string
  values: Array<{ seriesName: string; value: number; unit?: string | null }>
}

export type CartesianDataTable = {
  categoryHeader: string
  seriesHeaders: Array<{ name: string; unit?: string | null }>
  rows: CartesianDataTableRow[]
}

/** Formats chart values for display and screen readers (de-DE). */
export function formatChartValue(value: number): string {
  return value.toLocaleString('de-DE')
}

/** Formats a number as a percentage string (de-DE). */
export function formatChartPercent(value: number, total: number): string {
  if (total <= 0) return '0%'
  const ratio = value / total
  return `${(ratio * 100).toLocaleString('de-DE', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`
}

export function buildCartesianDataTable(
  chart: AccidentAtlasCartesianChartConfig
): CartesianDataTable {
  const seriesHeaders = chart.series.map((series) => ({
    name: series.name,
    unit: series.unit,
  }))

  const rows = chart.xAxis.categories.map((category, categoryIndex) => ({
    category,
    values: chart.series.map((series) => ({
      seriesName: series.name,
      value: series.data[categoryIndex] ?? 0,
      unit: series.unit,
    })),
  }))

  return {
    categoryHeader: chart.xAxis.name,
    seriesHeaders,
    rows,
  }
}

export type PieDataTableRow = {
  label: string
  percent: string
}

export function buildPieDataTable(
  chart: AccidentAtlasPieChartConfig
): PieDataTableRow[] {
  const values = chart.datasets[0]?.data ?? []
  const total = values.reduce((sum, value) => sum + value, 0)

  return chart.labels.map((label, index) => ({
    label,
    percent: formatChartPercent(values[index] ?? 0, total),
  }))
}

export function buildPieSummaryText(rows: PieDataTableRow[]): string {
  return rows.map((row) => `${row.label} ${row.percent}`).join(', ')
}

export function buildCartesianSummaryText(
  table: CartesianDataTable,
  renderType: Extract<AccidentAtlasChartRenderType, 'ColumnChart' | 'LineChart'>
): string {
  const valueSeparator = renderType === 'LineChart' ? '; ' : ', '

  return table.rows
    .map((row) => {
      const values = row.values
        .map((entry) => {
          const unitSuffix = entry.unit ? ` ${entry.unit}` : ''
          return `${entry.seriesName} ${formatChartValue(
            entry.value
          )}${unitSuffix}`
        })
        .join(', ')

      return `${row.category}: ${values}`
    })
    .join(valueSeparator)
}

export function formatDataTableCellValue(
  value: number,
  unit?: string | null
): string {
  const formatted = formatChartValue(value)
  return unit ? `${formatted} ${unit}` : formatted
}
